/**
 * src/RegistryCategoryPage.js
 *
 * VISINVOKE Windows Knowledge Centre - generic category page.
 *
 * Fully data-driven: the category is resolved from the :categoryId route
 * param against registryConfig, including its accent colour. Adding a future
 * category requires only a new entry in REGISTRY_CATEGORIES.
 *
 * Stage XX-B: expanded from a placeholder into the complete, permanent
 * knowledge-centre experience. Every section is structure only - no content,
 * no search, no filtering, no backend. The page is knowledge-base ready and
 * built around three content types (articles, registry tweaks, Windows tools),
 * not registry tweaks alone. Future stages populate content only.
 */
import React from 'react';
import { viGlassFill, viGlassBorder, viGlassShadow } from './config/glassTheme';
import { useParams, useNavigate } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import {
  RegistryInformationPanel,
  CategoryStats,
  QuickStartSection,
  FeaturedContentSection,
  RecentContentSection,
  PopularContentSection,
  KnowledgeLibrary,
  SafetyCentre,
  RelatedCategories,
  FAQSection,
  hexA,
  rg,
} from './RegistryComponents';
import {
  getRegistryCategory,
  getStatusColor,
  getRelatedCategories,
  REGISTRY_TWEAK_FIELDS,
  REGISTRY_DEFAULTS,
  REGISTRY_BASE_ROUTE,
  CATEGORY_STAT_DEFS,
  DIFFICULTY_LEVELS,
  LIBRARY_SORT_OPTIONS,
  WINDOWS_VERSIONS,
  KNOWLEDGE_CONTENT_TYPES,
  REGISTRY_FAQ,
  REGISTRY_SAFETY,
} from './registryConfig';

export default function RegistryCategoryPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const category = getRegistryCategory(categoryId);

  // Graceful fallback for an unknown category id.
  if (!category) {
    return (
      <div style={page.root}>
        <ProductNav />
        <div style={{ ...rg.section, padding: '60px 40px' }}>
          <h1 style={rg.heroTitle}>Category Not Found</h1>
          <p style={rg.sectionDesc}>
            This Knowledge Centre category does not exist yet. Return to the Windows Knowledge Centre overview to browse available categories.
          </p>
          <button
            style={rg.openBtn}
            onClick={() => navigate(REGISTRY_BASE_ROUTE)}
          >
            Back to Windows Knowledge Centre
          </button>
        </div>
      </div>
    );
  }

  const accent = category.accent || '#f8fafc';
  const status = category.status || REGISTRY_DEFAULTS.status;
  const statusColor = getStatusColor(status);
  const tweaksAvailable =
    category.tweaksAvailable !== undefined
      ? category.tweaksAvailable
      : REGISTRY_DEFAULTS.tweaksAvailable;
  const lastUpdated = category.lastUpdated || REGISTRY_DEFAULTS.lastUpdated;

  // Dynamic getting-started items, derived from the category + defaults.
  const quickStart = [
    { icon: '\u{1F4CB}', label: 'What this category covers', text: category.description },
    { icon: '\u{1F465}', label: 'Who it is designed for', text: `Windows users who want to customise and better understand ${category.title}, from curious beginners through to experienced power users.` },
    { icon: '\u{1F511}', label: 'Administrator privileges', text: 'Some changes in this category commonly require administrator rights. Each entry will state its requirement clearly before you begin.' },
    { icon: '\u{1FA9F}', label: 'Windows versions supported', text: `Entries target ${WINDOWS_VERSIONS.join(' and ')}. Every entry lists its exact compatibility.` },
    { icon: '\u{1F6E1}', label: 'Safety recommendations', text: 'Always back up first, create a System Restore point, and make sure you understand a change before applying it.' },
  ];

  const relatedCategories = getRelatedCategories(category.id, 4);

  const openCategory = cat => {
    navigate(`${REGISTRY_BASE_ROUTE}/${cat.id}`);
  };

  return (
    <div style={page.root}>
      <ProductNav />


      {/* Breadcrumb / back */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '22px 40px 0', width: '100%', boxSizing: 'border-box' }}>
        <button
          style={page.backLink}
          onClick={() => navigate(REGISTRY_BASE_ROUTE)}
          onMouseEnter={e => { e.currentTarget.style.color = accent; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#aab8c9'; }}
        >
          {'\u2190'} Windows Knowledge Centre
        </button>
      </div>

      {/* Title */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '10px 40px 0', width: '100%', boxSizing: 'border-box' }}>
        <div style={page.titleRow}>
          <span style={{ ...page.titleIcon, color: accent, textShadow: `0 0 18px ${hexA(accent, 0.6)}` }}>{category.icon}</span>
          <h1 style={page.title}>{category.title}</h1>
        </div>
        <p style={page.desc}>{category.description}</p>
      </div>

      {/* Hero info banner */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '18px 40px 0', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ ...page.banner, background: viGlassFill(accent, accent, accent), border: viGlassBorder(accent, 1.5), boxShadow: viGlassShadow(accent, accent, accent, false) }}>
          <div style={page.bannerRow}>
            <div style={page.bannerCell}>
              <span style={rg.metaLabel}>Status</span>
              <span style={{ ...rg.statusBadge, color: statusColor, borderColor: hexA(statusColor, 0.5), background: hexA(statusColor, 0.14) }}>
                {status}
              </span>
            </div>
            <div style={page.bannerCell}>
              <span style={rg.metaLabel}>Tweaks Available</span>
              <span style={rg.metaValue}>{tweaksAvailable}</span>
            </div>
            <div style={page.bannerCell}>
              <span style={rg.metaLabel}>Last Updated</span>
              <span style={rg.metaValue}>{lastUpdated}</span>
            </div>
          </div>
          <p style={page.bannerNote}>
            This category is part of the VISINVOKE Windows Knowledge Centre. Entries are documented, safely applied, and fully reversible. Content for this category will be added in a future update.
          </p>
        </div>
      </div>

      {/* Summary stat cards */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 40px 0', width: '100%', boxSizing: 'border-box' }}>
        <CategoryStats stats={CATEGORY_STAT_DEFS} accent={accent} />
      </div>

      {/* Stacked sections */}
      <div style={page.sections}>

        <QuickStartSection items={quickStart} accent={accent} />

        <FeaturedContentSection accent={accent} />

        <RecentContentSection accent={accent} />

        <PopularContentSection accent={accent} />

        <KnowledgeLibrary
          categoryFilters={KNOWLEDGE_CONTENT_TYPES.map(t => t.label)}
          difficultyLevels={DIFFICULTY_LEVELS}
          windowsVersions={WINDOWS_VERSIONS}
          sortOptions={LIBRARY_SORT_OPTIONS}
          accent={accent}
        />

        <SafetyCentre items={REGISTRY_SAFETY} accent={accent} />

        <RelatedCategories categories={relatedCategories} onOpen={openCategory} />

        <FAQSection faqs={REGISTRY_FAQ} />

        {/* Information sidebar content, kept as a full-width panel below */}
        <div style={page.infoWrap}>
          <RegistryInformationPanel
            title="Every entry will provide"
            lead="When content is added to this category, each entry will include the following details."
            items={REGISTRY_TWEAK_FIELDS}
            accent={accent}
          />
        </div>

      </div>
    </div>
  );
}

const page = {
  root: {
    minHeight: '100vh',
    background: '#0d1117',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  backLink: {
    background: 'transparent',
    border: 'none',
    color: '#aab8c9',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    transition: 'color 180ms ease',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  titleIcon: {
    fontSize: 34,
    lineHeight: 1,
  },
  title: {
    fontSize: 38,
    fontWeight: 900,
    color: '#ffffff',
    lineHeight: 1.15,
    margin: 0,
    letterSpacing: '-0.5px',
  },
  desc: {
    fontSize: 16,
    color: '#cbd5e1',
    lineHeight: 1.7,
    margin: '12px 0 0',
    maxWidth: 860,
  },
  banner: {
    background: 'linear-gradient(160deg, #101a2e 0%, #1e3a8a 45%, #1d4ed8 80%, #2563eb 100%)',
    borderRadius: 12,
    padding: '22px 24px',
  },
  bannerRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 32,
    marginBottom: 16,
  },
  bannerCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
    alignItems: 'flex-start',
  },
  bannerNote: {
    fontSize: 14,
    color: '#e2e8f0',
    lineHeight: 1.65,
    margin: 0,
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: 44,
    padding: '36px 0 72px',
  },
  infoWrap: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 40px',
    width: '100%',
    boxSizing: 'border-box',
  },
};
